import type { FC } from 'react';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkAvatarGroup from '@atlaskit/avatar-group';
import { Box, xcss } from '@atlaskit/primitives';

import {
	ContentStateStateContainer,
	EditorContentState,
	RendererContentState,
} from '@confluence/content-state';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { isExternalShareRequest } from '@confluence/external-share-utils';
import { fg } from '@confluence/feature-gating';
import { PageOwnershipByline } from '@confluence/content-ownership';
import { ProfileAvatar, ProfileLink } from '@confluence/profile';
import { useSpaceId } from '@confluence/space-utils';
import { LivePagesReactions } from '@confluence/live-pages-features/entry-points/LivePagesReactions';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { AnalyticsByLine } from '@confluence/confluence-analytics';

import { useByLineApps } from './useByLineApps';
import { LazyLivePagesByLineApps } from './LivePagesByLineApps';
import { png_1x1_n40 } from './png1x1';
import {
	StyledAuthorDiv,
	StyledAvatars,
	LivePagesStyledMainDiv,
	LivePagesWithNewBylineStyledMainDiv,
	StyledNameSkeleton,
	StyledTextDiv,
	StyledTextDivForNewByline,
	BylineAppsTextDiv,
} from './PresentationalComponents';
import type { UserShape } from './types';
import { getArrangedContributors } from './utils';
import { ByLineLastModified } from './ByLineLastModified';

const NO_OF_AVATARS = 4;

type Version = {
	friendlyWhen: string;
	number: number;
	by: UserShape;
	contentTypeModified: boolean;
};

export type LivePagesByLineWithContributorProps = {
	contentId: string;
	author: UserShape;
	owner: UserShape | null;
	lastOwner: UserShape | null;
	createdDate: Date | null;
	contributors: UserShape[];
	contributorsCount: number;
	fetchAllContributors: Function;
	lastModifyUser: UserShape;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	isKnowledgeBaseSpace: boolean;
	version: Version;
	viewMode?: boolean;
};

type userAvatarShape = {
	isAnonymous: boolean;
	userId: string;
	src: string;
	name: string;
};

const contentStateStyles = xcss({
	marginTop: 'space.025',
	marginLeft: 'space.050',
});

const pageViewsComponentStyles = xcss({
	marginTop: 'space.050',
});

export const LivePagesByLineWithContributorsComponent: FC<LivePagesByLineWithContributorProps> =
	memo(
		({
			contentId,
			author,
			owner,
			createdDate,
			lastModifyUser,
			contributors,
			contributorsCount,
			fetchAllContributors,
			lastOwner,
			contentType,
			isPublishingContent,
			userOffline,
			isKnowledgeBaseSpace,
			version,
			viewMode,
		}) => {
			const { createAnalyticsEvent } = useAnalyticsEvents();
			const isExternalShare = isExternalShareRequest();

			const spaceId = useSpaceId();
			const isNewLivePageBylineFeatureFlagEnabled = useBooleanFeatureFlag(
				'confluence.frontend.custom-sites.new-live-pages-byline',
			);
			const isCustomSitesPageTitleFFOn = useBooleanFeatureFlag(
				'confluence.frontend.custom-sites.page-header-and-title',
			);
			const isNewLivePageByline =
				isNewLivePageBylineFeatureFlagEnabled && isCustomSitesPageTitleFFOn;
			const isLivePageExtensionPointsEnabled = fg(
				'confluence_frontend_extension_points_in_live_pages',
			);

			const isLastUpdateEnabled = fg('live_pages_byline_last_updated');

			const fireAnalyticsEvent = useCallback(
				(action: any) => {
					// Analytics event is for measuring how many prefetches may be wasteful.
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action,
							actionSubject: 'moreButton',
							source: 'livePagesBylineContributors',
						},
					}).fire();
				},
				[createAnalyticsEvent],
			);

			const { hasApps, connectItems, forgeItems, forgeError } = useByLineApps(contentId);

			const getContributorsData = useMemo(() => {
				const arrangedContributors: UserShape[] = getArrangedContributors({
					owner,
					author,
					lastModifier: lastModifyUser,
					contributors,
				});

				const userToAvatarGroupDataItem: (user) => userAvatarShape = (user) => ({
					// Props for <ProfileAvatar>
					isAnonymous: user.isAnonymous,
					userId: user.userId,
					src: user.avatarUrl,
					// Extra props for <AkAvatarGroup>
					name: user.fullName,
				});

				return [
					...arrangedContributors
						// Actual data for the users we know about
						.map(userToAvatarGroupDataItem),
					// Skeletons for the ones we don't
					...Array(Math.max(contributorsCount - arrangedContributors.length, 0))
						.fill(null)
						.map(() => ({
							src: png_1x1_n40,
							name: <StyledNameSkeleton />,
						})),
				];
			}, [owner, author, lastModifyUser, contributors, contributorsCount]);

			const prefetchContributors = useCallback(() => {
				if (contributorsCount === contributors.length) return;
				if (!fetchAllContributors) return;

				fireAnalyticsEvent('dataPrefetched');
				fetchAllContributors();
			}, [contributorsCount, contributors.length, fetchAllContributors, fireAnalyticsEvent]);

			const fetchContributors = useCallback(() => {
				// Always fire 'dataNeeded' when a click/tap on the +x button happened,
				// regardless of whether we still need to fetch more data.
				fireAnalyticsEvent('dataNeeded');
				if (contributorsCount === contributors.length) return;
				if (!fetchAllContributors) return;
				fetchAllContributors();
			}, [fireAnalyticsEvent, contributorsCount, contributors.length, fetchAllContributors]);

			const showMoreButtonProps = useMemo(
				() => ({
					onMouseEnter: prefetchContributors,
					onClick: fetchContributors,
				}),
				[fetchContributors, prefetchContributors],
			);

			const CreatorOrOwnerByline = useMemo(() => {
				if (owner !== null) {
					return (
						<PageOwnershipByline
							owner={owner}
							creator={author}
							createdDate={createdDate}
							templateId={null}
							contentId={contentId}
							lastOwner={lastOwner}
						/>
					);
				}

				return (
					<FormattedMessage
						id="content-header.livepages.by-line.created.by.expanded"
						defaultMessage="Created by {author}"
						description="Text to show author details"
						values={{
							author: (
								<ProfileLink
									userId={author.userId}
									fullName={author.fullName}
									isAnonymous={author.isAnonymous}
									referralSource="profilecard"
								/>
							),
						}}
					/>
				);
			}, [author, owner, createdDate, contentId, lastOwner]);

			const BylineMainDivComponent = isNewLivePageByline
				? LivePagesWithNewBylineStyledMainDiv
				: LivePagesStyledMainDiv;
			const BylineTextDivComponent = isNewLivePageByline
				? StyledTextDivForNewByline
				: StyledTextDiv;

			const toolbarButtonStyle = isNewLivePageByline && { height: '24px', alignItems: 'center' };

			return (
				<BylineMainDivComponent>
					<StyledAvatars>
						<AkAvatarGroup
							appearance="stack"
							// @ts-ignore
							data={getContributorsData}
							avatar={ProfileAvatar}
							maxCount={NO_OF_AVATARS}
							size="small"
							showMoreButtonProps={showMoreButtonProps}
							testId="contributors-avatar-group"
						/>
					</StyledAvatars>
					<div>
						<BylineTextDivComponent data-testId="byline-text-component">
							<StyledAuthorDiv>{CreatorOrOwnerByline}</StyledAuthorDiv>
							{!isExternalShare &&
								!isNewLivePageByline &&
								isLivePageExtensionPointsEnabled &&
								hasApps && (
									<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
										<LazyLivePagesByLineApps
											contentId={contentId}
											connectItems={connectItems}
											forgeItems={forgeItems}
											forgeError={forgeError}
											showLivePagesExtensionPoints
										/>
									</ErrorBoundary>
								)}
							{isNewLivePageByline && (
								<>
									<Box xcss={contentStateStyles}>
										<Subscribe to={[ContentStateStateContainer]}>
											{(contentStateState: ContentStateStateContainer) => {
												// view mode users should not be able to edit the content state, so we just render the content state.
												if (viewMode) {
													return (
														<RendererContentState
															contentId={contentId}
															hasSeparator={false}
															isPagePreview // this sets the query to use draft version over published version
														/>
													);
												}
												// Knowledge space live pages will always show the editable content state component.
												// Other live pages will only show the editable content state component if the content state is already set.
												// This is because a page without a content state is initialized by the ContentStateHoverButton component.
												return (
													(contentStateState.state.contentState || isKnowledgeBaseSpace) && (
														<EditorContentState
															contentId={contentId}
															contentType={contentType}
															isPublishingContent={isPublishingContent}
															userOffline={userOffline}
															isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
															floatingToolbarButtonStyle={toolbarButtonStyle}
														/>
													)
												);
											}}
										</Subscribe>
									</Box>
									{isLastUpdateEnabled && (
										<ByLineLastModified version={version} author={author} contentId={contentId} />
									)}
									<Box xcss={pageViewsComponentStyles}>
										<AnalyticsByLine contentId={contentId} />
									</Box>
									<LivePagesReactions
										contentId={contentId}
										spaceId={spaceId || ''}
										isDisabled={false}
										isContentCreationPanelOpen
									/>
								</>
							)}
						</BylineTextDivComponent>
						{!isExternalShare &&
							isNewLivePageByline &&
							isLivePageExtensionPointsEnabled &&
							hasApps && (
								<BylineAppsTextDiv>
									<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
										<LazyLivePagesByLineApps
											contentId={contentId}
											connectItems={connectItems}
											forgeItems={forgeItems}
											forgeError={forgeError}
											showLivePagesExtensionPoints
										/>
									</ErrorBoundary>
								</BylineAppsTextDiv>
							)}
					</div>
				</BylineMainDivComponent>
			);
		},
	);
